import { RUNNER_RELEASE_CHANNELS, type RunnerReleaseChannel } from '@developerz/domain';
import { createEffect, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { releaseVersions, useReleases } from '../lib/box-lifecycle';
import { createRolloutForm } from '../lib/fleet-rollout';
import { Field } from './Field';
import { FleetPromoteSection } from './FleetPromoteSection';
import { Modal } from './Modal';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// Roll the fleet onto a build (`POST /v1/runners/update`). Container-ish: it
// owns the per-channel release read that fills the pin picker; the write state
// is `createRolloutForm`.
//
// THE CHANNEL IS REQUIRED AND NEVER DEFAULTED — the rule the route, the verb and
// the form all hold. The select opens on a placeholder, the trigger stays
// disabled until a human picks, and the version read IDLES meanwhile: `canary`
// is the slice a staged rollout soaks on and `stable` is every box, so a
// pre-selected dropdown would be a default wearing a control's clothes.
//
// PINNING IS OPTIONAL AND ITS READ IS NOT A GATE. Omitting the version rolls the
// channel's newest installable build, which is the ordinary case; a ledger read
// that fails or serves nothing therefore leaves the trigger live and says so,
// rather than blocking a rollout on a picker nobody has to use.
//
// A 202 IS NOT AN UPDATED FLEET. `started: false` means a rollout was already
// running on this channel and this trigger joined it — reported as itself, with
// the version ACTUALLY rolling, which is not necessarily the one just asked for.

export function FleetRolloutModal(props: {
  readonly open: boolean;
  readonly token: string | null;
  /**
   * `/v1/me` says `is_platform_admin` (#6735): the staff-only promote section shows.
   * Absent or false, a non-staff admin sees no promote control at all.
   */
  readonly staff?: boolean;
  readonly onClose: () => void;
  readonly onStarted: () => void;
}): JSX.Element {
  const form = createRolloutForm({ token: props.token, onStarted: props.onStarted });
  const releases = useReleases(form.channel, props.token);
  const versions = (): readonly string[] => releaseVersions(releases.data);
  // What the picker can honestly say about itself. A FAILED read is checked
  // first: an empty list from a 403/500 is no answer, and "no published builds"
  // there was a claim about the channel made from nothing (19-9). A TRUNCATED
  // read is a head of the ledger, never the whole of it, so an older build the
  // operator came to roll back to is named as not listed rather than as absent.
  const versionDesc = (): string => {
    if (!form.ready()) return t('fleet.rollout.version_desc');
    if (releases.isError) return t('fleet.rollout.version_error');
    if (releases.isLoading) return t('fleet.rollout.version_desc');
    if (versions().length === 0) return t('fleet.rollout.version_none');
    return releases.data?.truncated === true
      ? t('fleet.rollout.version_truncated')
      : t('fleet.rollout.version_desc');
  };

  // Re-opening starts from no channel: the previous outcome belongs to the
  // rollout that produced it, and a carried-over pick is a default again.
  createEffect(
    on(
      () => props.open,
      (open) => {
        if (open) form.reset();
      },
    ),
  );

  return (
    <Modal
      open={props.open}
      size="lg"
      title={t('fleet.rollout.title')}
      desc={t('fleet.rollout.desc')}
      onClose={props.onClose}
    >
      <form
        class="field-group"
        onSubmit={(e) => {
          e.preventDefault();
          form.submit();
        }}
      >
        <Field
          label={t('fleet.rollout.channel')}
          id="rollout-channel"
          desc={t('fleet.rollout.channel_desc')}
        >
          {(a11y) => (
            <Select<RunnerReleaseChannel | ''>
              a11y={a11y}
              label={t('fleet.rollout.channel')}
              block
              value={form.channel() ?? ''}
              options={[
                { value: '', label: t('fleet.rollout.channel_placeholder') },
                ...RUNNER_RELEASE_CHANNELS.map((channel) => ({ value: channel, label: channel })),
              ]}
              onChange={(value) => form.pick(value === '' ? null : value)}
            />
          )}
        </Field>

        <Field label={t('fleet.rollout.version')} id="rollout-version" desc={versionDesc()}>
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('fleet.rollout.version')}
              block
              disabled={!form.ready()}
              value={form.version()}
              options={[
                { value: '', label: t('fleet.rollout.version_newest') },
                ...versions().map((version) => ({ value: version, label: version })),
              ]}
              onChange={form.setVersion}
            />
          )}
        </Field>

        <Show when={form.error()}>
          {(msg) => (
            <p class="field__error" role="alert">
              {msg()}
            </p>
          )}
        </Show>

        <Show when={form.started()}>
          {(started) => (
            <div class="alert" role="status">
              <p class="alert__title">
                {t(
                  started().started
                    ? 'fleet.rollout.result_started'
                    : 'fleet.rollout.result_joined',
                  { channel: started().channel },
                )}
              </p>
              <p class="alert__desc">
                {t('fleet.rollout.result_version', {
                  version: started().version ?? t('fleet.rollout.result_version_newest'),
                })}
              </p>
              <p class="alert__desc">{t('fleet.rollout.result_watch')}</p>
            </div>
          )}
        </Show>

        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('fleet.rollout.close')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={!form.ready() || form.pending()}>
            {t(form.pending() ? 'fleet.rollout.submitting' : 'fleet.rollout.submit')}
          </button>
        </div>
      </form>
      <Show when={props.staff === true}>
        <FleetPromoteSection token={props.token} />
      </Show>
    </Modal>
  );
}
